import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import { UserMenuService } from '@api/user-menu.service';
import { ResourceSeriesResponse, ResourceSeriesVM, SeriesAggregation } from '@models/backend-generated-models/models';
import { withColours } from '../resource-series-chart/series-util';
import { formatValue, ReportKind, reportKind } from '../resource-series-chart/format';
import { computeTotals, seriesFigure } from '../resource-series-chart/totals';

/** One slice: a series, its total over the window, and the colour Carbon Admin gave it. */
interface Slice {
  name: string;
  value: number;
  rgb: string;
  /** Share of the whole, 0-100. Held rather than computed in the template so it is worked out once. */
  percent: number;
}

/**
 * The breakdown pie, for categories whose ShowFormat offers "P".
 *
 * Counted on production 21/09/2026, 266 of the 301 FPCategories ask for a pie - it is not a side
 * feature, it is what most categories open on. The SSRS reports have had one all along and the
 * native chart has never had one, which is why the Bar/Pie toggle had to be hidden behind
 * *ngIf="!useNativeChart" in a1cbd29.
 *
 * This deliberately does its own fetch rather than reading the bar chart's. The two are never on
 * screen together, the request is the same one either way, and a pie that owns its data does not
 * have to be plumbed through a component the solar/battery work is actively refactoring.
 */
@Component({
  selector: 'app-resource-series-pie',
  templateUrl: './resource-series-pie.component.html',
  styleUrls: ['./resource-series-pie.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResourceSeriesPieComponent implements OnChanges, OnDestroy {

  @Input() siteId!: number;
  @Input() readingTypeId!: number;
  @Input() period: string = '7d';
  @Input() dateLeft!: string;
  @Input() dateRight!: string;

  /** The category's own report. Decides which proc answers - see the service method. */
  @Input() reportUrl?: string;

  /** The selected group's parameter name, straight off GroupVM.Type. */
  @Input() groupType?: string;
  @Input() groupId?: number;

  isLoading = false;
  errorMessage: string | null = null;

  slices: Slice[] = [];
  chartData: { name: string; value: number }[] = [];
  colours: Color = { name: 'resource-pie', selectable: true, group: ScaleType.Ordinal, domain: [] };

  /** From ReadingTypes.Unit, never assumed - this also renders m3, Litre, kg of CO2. */
  unit = '';

  total = 0;

  /**
   * Set when a consumption window contains an export hour.
   *
   * A pie divides a whole into parts, and a negative part has no share of anything: two series of
   * +100 and -40 would draw as 71% and 29% of a 140 that was never consumed. The SSRS consumption
   * report refuses on exactly this test - its pie is hidden when Sum(IIf(Value &lt; 0, 1, 0)) &gt; 0 -
   * so this says so out loud rather than drawing a wrong picture. Only that report: the remainder,
   * policy and status pies had no such rule, and a single negative policy day used to blank them
   * all. Elsewhere a group whose window comes to less than nothing simply has no slice.
   */
  hasNegativeValues = false;

  /** The SSRS report this pie stands in for, by the way it writes its numbers. */
  get kind(): ReportKind {
    return reportKind(this.reportUrl);
  }

  /** Averaged reading types are labelled, because a pie of averages is a different claim. */
  isAverage = false;

  /**
   * Explicit size for the plot, rather than letting ngx-charts measure its own parent.
   *
   * Left to itself it measured 212px inside a 297px box and drew the doughnut in the top two
   * thirds, which put the total in the middle of the BOX and a good 40px below the middle of the
   * RING. Sizing it from the container makes the two centres the same point by construction.
   */
  view: [number, number] = [600, 320];

  /**
   * A setter rather than a plain ViewChild: the plot lives inside an *ngIf, so on a window with no
   * readings it does not exist at ngAfterViewInit and appears later, when a date change brings data
   * back. Attaching the observer as it arrives covers both.
   */
  @ViewChild('plot') set plot(el: ElementRef<HTMLElement> | undefined) {
    this.plotRef = el;
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;

    if (!el) return;

    this.measure();

    // The dialog is resizable and the legend's width changes with the longest group name, so the
    // plot cannot be measured once and left.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(el.nativeElement);
    }
  }

  private plotRef?: ElementRef<HTMLElement>;
  private resizeObserver?: ResizeObserver;
  private sub?: Subscription;

  constructor(private userMenuService: UserMenuService, private cdr: ChangeDetectorRef) { }

  ngOnChanges(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  private measure(): void {
    const el = this.plotRef?.nativeElement;
    if (!el) return;

    const width = Math.floor(el.clientWidth);
    const height = Math.floor(el.clientHeight);
    if (width <= 0 || height <= 0) return;

    if (this.view[0] !== width || this.view[1] !== height) {
      this.view = [width, height];
      // detectChanges rather than markForCheck: the observer fires outside Angular's own change
      // detection, so a mark alone leaves the new size unpainted until something else ticks.
      this.cdr.detectChanges();
    }
  }

  private load(): void {
    if (!this.siteId || !this.readingTypeId || !this.dateLeft || !this.dateRight) return;

    // One request in flight at a time. Without this, dragging a date picker leaves whichever
    // response happens to land last painting the pie.
    this.sub?.unsubscribe();

    this.isLoading = true;
    this.errorMessage = null;

    this.sub = this.userMenuService.getResourceSeries$(
      this.siteId,
      this.readingTypeId,
      this.period,
      this.dateLeft,
      this.dateRight,
      this.reportUrl,
      this.groupType,
      this.groupId
    ).subscribe({
      next: (response: ResourceSeriesResponse) => {
        this.apply(response);
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'The breakdown could not be loaded.';
        this.slices = [];
        this.chartData = [];
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private apply(response: ResourceSeriesResponse): void {
    this.unit = response?.unit ?? '';
    // The status pie follows its reading type, as rptResourceStatus's Chart2 did
    // (IIf(ReadingTypeGroupID = 1, Avg(AvgValue), Sum(Value))), not the value types the bars follow.
    this.isAverage = this.kind === 'status'
      ? response?.readingTypeAggregation === SeriesAggregation.Average
      : response?.aggregation === SeriesAggregation.Average;

    const series = withColours(response?.series ?? []);

    this.hasNegativeValues = this.kind === 'consumption'
      && series.some(s => (s.points ?? []).some(p => Number(p.value) < 0));

    if (this.hasNegativeValues) {
      this.slices = [];
      this.chartData = [];
      this.total = 0;
      return;
    }

    const slices: Slice[] = [];

    for (const s of series) {
      const value = this.combine(s);

      // A series that contributed nothing has no slice. Drawing a zero-width wedge only adds a
      // legend row and a colour that never appears in the circle.
      if (!(value > 0)) continue;

      slices.push({
        name: s.name || 'Unnamed',
        value,
        rgb: s.rgb || '#cbd5e1',
        percent: 0
      });
    }

    // Largest first. The SSRS pie draws in whatever order the proc returned, which puts the biggest
    // slice wherever it happens to fall and makes two windows of the same site hard to compare.
    slices.sort((a, b) => b.value - a.value);

    const sum = slices.reduce((a, s) => a + s.value, 0);
    for (const s of slices) {
      s.percent = sum > 0 ? (s.value / sum) * 100 : 0;
    }

    // The middle says the chart's own total. On a remainder that is the mains meter's reading, as the
    // SSRS pie titled itself "Total: 256", where the slices can add up to more (see totals.ts).
    const totals = computeTotals(series, { kind: this.kind, averaged: this.isAverage, metered: true });
    this.total = this.kind === 'remainder' && totals.total !== null ? totals.total : sum;

    this.slices = slices;
    this.chartData = slices.map(s => ({ name: s.name, value: s.value }));

    // ngx-charts takes colour by POSITION in this domain, not by name, so it is built from the
    // slices in the order they are emitted. Any reordering above has to happen before this line.
    this.colours = {
      name: 'resource-pie',
      selectable: true,
      group: ScaleType.Ordinal,
      domain: slices.map(s => s.rgb)
    };
  }

  /**
   * The figure in the middle: the total, or for averaged readings the average across the slices.
   * Three rooms at 20 °C are 20 °C on average, not "60 average".
   */
  get centreValue(): number {
    return this.isAverage && this.slices.length ? this.total / this.slices.length : this.total;
  }

  /**
   * One series into one number.
   *
   * Which way round is not ours to choose: temperature, current, voltage, humidity, frequency and
   * kVA are averaged and everything else is summed, and the server says which by reading the same
   * ReadingTypeGroupID the reports read. Summing a month of temperatures would give a slice in the
   * thousands of degrees.
   */
  private combine(s: ResourceSeriesVM): number {
    if (!(s.points ?? []).length) return 0;
    // Weighted by readings where the server has them, not the mean of the bucket averages. Not by
    // value type on the status pie: see apply.
    const own = this.kind === 'status' ? { ...s, valueTypeId: undefined } : s;
    return seriesFigure(own, this.isAverage, true);
  }

  /** Shown on the slice labels and the legend, as the chart's report writes its numbers. */
  format(value: number): string {
    return formatValue(value, this.kind, this.isAverage);
  }

  trackBySlice(_: number, slice: Slice): string {
    return slice.name;
  }
}
